<template>
  <div class="create-material">
    <h1>Create a Material</h1>
    <form @submit.prevent="submitForm">
      <div class="form-group">
        <label for="name">Name</label>
        <input
            type="text"
            id="name"
            v-model="material.name"
            @input="validateName"
            :class="{'is-invalid': validationErrors.includes('name')}"
            required
            class="form-control"
        />
        <div v-if="validationErrors.includes('name')" class="error-feedback">
          Name is required.
        </div>
      </div>

      <div class="form-group">
        <label for="batch">Batch</label>
        <input
            type="text"
            id="batch"
            v-model="material.batch"
            @input="validateBatch"
            :class="{'is-invalid': validationErrors.includes('batch')}"
            required
            class="form-control"
        />
        <div v-if="validationErrors.includes('batch')" class="error-feedback">
          Batch is required.
        </div>
      </div>

      <div class="form-group">
        <label for="projects">Projects (Optional)</label>
        <multiselect
          v-model="selectedProjects"
          :options="projects"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="Select one or more projects"
          track-by="id"
          label="name"
          class="form-control"
        />
      </div>

      <button type="submit" class="submit-button" :disabled="loading">
        <span v-if="loading" class="spinner"></span>
        <span v-else>Create Material</span>
      </button>
    </form>

    <div v-if="loading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { mapState } from 'vuex';
import axios from "axios";
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Multiselect from 'vue-multiselect';

const FORM_CACHE_KEY = "material_form";
const CACHE_EXPIRATION = 10 * 60 * 1000; // 10 minutes

export default {
  data() {
    return {
      material: {
        name: '',
        batch: '',
      },
      projects: [],
      selectedProjects: [],
      validationErrors: [],
      loading: false,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.loadCachedFormData();
    this.fetchProjects();
  },
  components: {
    Multiselect
  },
  methods: {
    loadCachedFormData() {
      const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
      if (cachedData) {
        const { data, timestamp } = cachedData;
        if (Date.now() - timestamp < CACHE_EXPIRATION) {
          this.material = data;
        } else {
          localStorage.removeItem(FORM_CACHE_KEY);
        }
      }
    },
    cacheFormData() {
      localStorage.setItem(
          FORM_CACHE_KEY,
          JSON.stringify({ data: this.material, timestamp: Date.now() })
      );
    },
    validateName() {
      this.validateField('name');
    },
    validateBatch() {
      this.validateField('batch');
    },
    validateField(field) {
      if (!this.material[field]) {
        this.addValidationError(field);
      } else {
        this.removeValidationError(field);
      }
    },
    addValidationError(field) {
      if (!this.validationErrors.includes(field)) {
        this.validationErrors.push(field);
      }
    },
    removeValidationError(field) {
      this.validationErrors = this.validationErrors.filter(f => f !== field);
    },
    validateForm() {
      this.validationErrors = [];
      ['name', 'batch'].forEach(f => {
        if (!this.material[f]) this.addValidationError(f);
      });
      return this.validationErrors.length === 0;
    },
    async submitMaterialDetails() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        return;
      }

      try {
        const response = await apiService.createResource(
            API_ENDPOINTS.materials,
            this.material,
            this.loggedInUser.entra_id
        );
        for (const project of this.selectedProjects) {
          await axios.post(API_ENDPOINTS.materialProject, {
            project_id: project.id,
            material_id: response.id,
          });
        }
        this.toast.success("Material created successfully!");
        localStorage.removeItem(FORM_CACHE_KEY);
        this.$router.push({
          name: 'MaterialDetailPage',
          params: { id: response.id }
        });
      } catch (error) {
        this.toast.error("Failed to create material!");
        console.error("Material creation error:", error);
      }
    },
    async submitForm() {
      this.loading = true;
      await this.submitMaterialDetails();
      this.loading = false;
    },
    async fetchProjects() {
      try {
        const response = await axios.get(API_ENDPOINTS.projects, {
          params: { entra_id: this.loggedInUser.entra_id },
        });
        this.projects = response.data;
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    },
  },
  watch: {
    material: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";


.create-material {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin-bottom: 2rem;
    text-align: center;
  }
}
</style>
